// Doors, glazed panes and openings cut into a user-drawn wall (`Wall.features`).
//
// Only parameters are stored — a segment index and two stations along it. The
// cut, the jambs, the pane, the swing and the vertical layers are derived here,
// every read, so reshaping the wall carries its features along for free. Plan,
// hit test, 3D, section and DXF all read these functions, so none can disagree
// about where a feature is.

import type { DoorSwing } from './doorSwing';
import type {
  Vec2,
  Wall,
  WallFeature,
} from '../shared/types';
import {
  polylineIsClosed,
  wallBand,
} from './wallTool';
import {
  wallBase,
  wallHeight,
} from './heights';
import {
  CUT_FACE_TOL,
  CUT_SQUARE,
  cutBand,
  type InWallPart,
} from './wallCuts';

export const WALL_DOOR_WIDTH = 800;
export const WALL_DOOR_HEIGHT = 2100;
export const WALL_DOOR_MIN = 300;
export const WALL_FEATURE_MIN = 50;
export const WALL_OPENING_SILL = 1000;
export const WALL_OPENING_TOP = 2100;
export const WALL_GLASS_THICKNESS = 10;

type WallLike = Pick<Wall, 'points' | 'thickness' | 'align'> &
  Partial<Pick<Wall, 'features' | 'style' | 'height' | 'heightFromGround'>>;

/** One centre-line segment of a wall: its direction `u`, left normal `n`, the two
 *  faces' offsets from the line (`oL` left, `oR` right, both ≥ 0) and the stations
 *  `[lo, hi]` where both faces run straight — a feature stays inside them, clear
 *  of the mitred corners. */
export interface WallSegFrame {
  a: Vec2;
  b: Vec2;
  u: Vec2;
  n: Vec2;
  len: number;
  oL: number;
  oR: number;
  lo: number;
  hi: number;
}

/** A feature as it is drawn: clamped into its segment and clear of its neighbours. */
export interface WallFeatureSpan {
  f: WallFeature;
  seg: number;
  s: number;
  e: number;
  fr: WallSegFrame;
}

function faceOffsets(w: Pick<Wall, 'thickness' | 'align'>): [number, number] {
  const t = Math.max(0, w.thickness);
  return [w.align === 'center' ? t / 2 : w.align === 'left' ? t : 0, w.align === 'center' ? t / 2 : w.align === 'right' ? t : 0];
}

/** The point at station `s` along the segment, `off` along its left normal. */
export function segPoint(fr: WallSegFrame, s: number, off: number): Vec2 {
  return { x: fr.a.x + fr.u.x * s + fr.n.x * off, y: fr.a.y + fr.u.y * s + fr.n.y * off };
}

export function wallSegFrame(w: WallLike, seg: number, band: Vec2[] = wallBand(w.points, w.thickness, w.align)): WallSegFrame | null {
  const p = w.points;
  if (seg < 0 || seg + 1 >= p.length) return null;
  const a = p[seg], b = p[seg + 1];
  const len = Math.hypot(b.x - a.x, b.y - a.y);
  if (len < 1e-6) return null;
  const u = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
  const n = { x: -u.y, y: u.x };
  const [oL, oR] = faceOffsets(w);
  const m = p.length;
  const along = (q: Vec2): number => (q.x - a.x) * u.x + (q.y - a.y) * u.y;
  let lo = 0, hi = len;
  if (band.length === 2 * m) {
    // `wallBand` is [...leftFace, ...rightFace.reversed()], one corner per vertex.
    lo = Math.max(0, along(band[seg]), along(band[2 * m - 1 - seg]));
    hi = Math.min(len, along(band[seg + 1]), along(band[2 * m - 2 - seg]));
  }
  return { a, b, u, n, len, oL, oR, lo, hi: Math.max(lo, hi) };
}

/** Every feature of a band wall as drawn, in order along the wall. A feature is
 *  clamped into its segment's straight run; one overlapping the feature before it
 *  is trimmed to start where that one ends (and dropped if nothing is left). A
 *  feature on a segment that no longer exists is skipped — the stored value is
 *  never mutated, so an undo brings it back. */
export function wallFeatureSpans(w: WallLike): WallFeatureSpan[] {
  if (w.style === 'line' || !w.features?.length || w.points.length < 2) return [];
  const band = wallBand(w.points, w.thickness, w.align);
  const frames = new Map<number, WallSegFrame | null>();
  const out: WallFeatureSpan[] = [];
  for (const f of w.features) {
    if (!frames.has(f.seg)) frames.set(f.seg, wallSegFrame(w, f.seg, band));
    const fr = frames.get(f.seg);
    if (!fr) continue;
    const s = Math.max(fr.lo, Math.min(fr.hi, Math.min(f.start, f.end)));
    const e = Math.max(fr.lo, Math.min(fr.hi, Math.max(f.start, f.end)));
    if (e - s < 1) continue;
    out.push({ f, seg: f.seg, s, e, fr });
  }
  out.sort((p, q) => p.seg - q.seg || p.s - q.s);
  const kept: WallFeatureSpan[] = [];
  for (const sp of out) {
    const prev = kept[kept.length - 1];
    if (prev && prev.seg === sp.seg && sp.s < prev.e) {
      sp.s = prev.e;
      if (sp.e - sp.s < 1) continue;
    }
    kept.push(sp);
  }
  return kept;
}

/** A part's flush box cut into a wall (`wallCut`; wallCuts.ts): on segment
 *  `seg` from station `s` to `e`, from `z0` to `z1`, into the left face
 *  (`side` 1) or the right one (-1), `depth` deep — `through` when that is the
 *  whole wall. */
export interface WallPocket {
  src: string;
  seg: number;
  s: number;
  e: number;
  z0: number;
  z1: number;
  side: 1 | -1;
  depth: number;
  through: boolean;
}

/** The pockets the parts set into a band wall cut in it: each part's cut where
 *  its face lies on one of the wall's faces (within `CUT_FACE_TOL`, square to
 *  it), clamped into its segment's straight run and inside the wall's height —
 *  a pocket never reaches the wall's top nor its bottom. One clashing with a
 *  door, a pane, an opening or a pocket before it is left out. */
export function wallPockets(w: WallLike, parts: readonly InWallPart[]): WallPocket[] {
  if (w.style === 'line' || w.points.length < 2 || !parts.length) return [];
  const band = wallBand(w.points, w.thickness, w.align);
  const spans = wallFeatureSpans(w);
  const b = wallBase(w), T = b + wallHeight(w);
  const out: WallPocket[] = [];
  for (const part of parts) {
    const cut = part.wallCut;
    if (!cut || !(cut.width > 0) || !(cut.height > 0)) continue;
    for (let i = 0; i + 1 < w.points.length; i++) {
      const fr = wallSegFrame(w, i, band);
      if (!fr) continue;
      const rel = { x: part.at.x - fr.a.x, y: part.at.y - fr.a.y };
      const t = rel.x * fr.u.x + rel.y * fr.u.y, off = rel.x * fr.n.x + rel.y * fr.n.y;
      const dot = part.into.x * fr.n.x + part.into.y * fr.n.y;
      const side = dot < -CUT_SQUARE && Math.abs(off - fr.oL) <= CUT_FACE_TOL ? 1 : dot > CUT_SQUARE && Math.abs(off + fr.oR) <= CUT_FACE_TOL ? -1 : 0;
      if (!side || t < fr.lo || t > fr.hi) continue;
      const s = Math.max(fr.lo, t - cut.width / 2), e = Math.min(fr.hi, t + cut.width / 2);
      const [c0, c1] = cutBand(part, cut);
      const z0 = Math.max(b + 1, c0), z1 = Math.min(T - 1, c1);
      if (e - s < 1 || z1 - z0 < 1) break;
      const thick = fr.oL + fr.oR, depth = Math.min(cut.depth ?? thick, thick);
      const clash = spans.some((sp) => sp.seg === i && sp.s < e && s < sp.e)
        || out.some((pk) => pk.seg === i && pk.s < e && s < pk.e && pk.z0 < z1 && z0 < pk.z1);
      if (!clash && depth > 0) out.push({ src: part.src, seg: i, s, e, z0, z1, side, depth, through: depth >= thick - 0.5 });
      break;
    }
  }
  return out;
}

/** A pocket's plan quad, from the face it is cut into to its back (the whole
 *  wall, through): [face s, face e, back e, back s]. */
export function wallPocketQuad(fr: WallSegFrame, pk: Pick<WallPocket, 's' | 'e' | 'side' | 'depth' | 'through'>): Vec2[] {
  const [face, back] = pk.through ? [fr.oL, -fr.oR] : pk.side > 0 ? [fr.oL, fr.oL - pk.depth] : [-fr.oR, -fr.oR + pk.depth];
  return [segPoint(fr, pk.s, face), segPoint(fr, pk.e, face), segPoint(fr, pk.e, back), segPoint(fr, pk.s, back)];
}

/** The cut across the full wall thickness: [left s, left e, right e, right s]. */
export function wallFeatureQuad(sp: Pick<WallFeatureSpan, 's' | 'e' | 'fr'>): Vec2[] {
  const { fr, s, e } = sp;
  return [segPoint(fr, s, fr.oL), segPoint(fr, e, fr.oL), segPoint(fr, e, -fr.oR), segPoint(fr, s, -fr.oR)];
}

/** A glazed pane: a `WALL_GLASS_THICKNESS` band on the middle of the wall. */
export function wallPaneQuad(sp: Pick<WallFeatureSpan, 's' | 'e' | 'fr'>): Vec2[] {
  const { fr, s, e } = sp;
  const c = (fr.oL - fr.oR) / 2, h = Math.min(WALL_GLASS_THICKNESS, fr.oL + fr.oR) / 2;
  return [segPoint(fr, s, c + h), segPoint(fr, e, c + h), segPoint(fr, e, c - h), segPoint(fr, s, c - h)];
}

/**
 * The solid stretches of the wall, each as `[outline, ...holes]`: its band cut at
 * every feature. Each run walks the left face from one cut to the next — through
 * the mitred corners in between — and back along the right face, so an open wall
 * with no features is exactly its `wallBand`, and a run spanning a corner keeps
 * the mitre. A CLOSED wall (`polylineIsClosed`) with no features is one ring, its
 * outer face with the inner face as a hole; with features, the run through its
 * joint is one piece. Outlines run counter-clockwise and holes clockwise, so the
 * default nonzero fill reads them right.
 */
export function wallSolidRuns(w: WallLike): Vec2[][][] {
  const band = wallBand(w.points, w.thickness, w.align);
  if (band.length < 3) return [];
  const m = w.points.length;
  const closed = polylineIsClosed(w.points);
  const L = (i: number): Vec2 => band[i];
  const R = (i: number): Vec2 => band[2 * m - 1 - i];
  const spans = wallFeatureSpans(w);
  if (!spans.length) {
    if (!closed) return [[orient(band, 1)]];
    const faces = [band.slice(0, m - 1), Array.from({ length: m - 1 }, (_, i) => R(i))]
      .sort((a, b) => Math.abs(signedArea(b)) - Math.abs(signedArea(a)));
    return [[orient(faces[0], 1), orient(faces[1], -1)]];
  }
  type Cut = { seg: number; s: number; cap?: 'start' | 'end'; fr?: WallSegFrame };
  const lastLen = Math.hypot(w.points[m - 1].x - w.points[m - 2].x, w.points[m - 1].y - w.points[m - 2].y);
  const cuts: Array<[Cut, Cut]> = [];
  let from: Cut = { seg: 0, s: 0, cap: 'start' };
  for (const sp of spans) {
    cuts.push([from, { seg: sp.seg, s: sp.s, fr: sp.fr }]);
    from = { seg: sp.seg, s: sp.e, fr: sp.fr };
  }
  cuts.push([from, { seg: m - 2, s: lastLen, cap: 'end' }]);
  const side = (c: Cut, left: boolean): Vec2 =>
    c.cap === 'start' ? (left ? L(0) : R(0))
      : c.cap === 'end' ? (left ? L(m - 1) : R(m - 1))
        : segPoint(c.fr!, c.s, left ? c.fr!.oL : -c.fr!.oR);
  const runs: Array<{ left: Vec2[]; right: Vec2[]; a: Cut; b: Cut }> = [];
  for (const [a, b] of cuts) {
    if (a.seg === b.seg && b.s - a.s < 0.5) continue;
    const left = [side(a, true)], right = [side(a, false)];
    for (let k = a.seg + 1; k <= b.seg; k++) { left.push(L(k)); right.push(R(k)); }
    left.push(side(b, true));
    right.push(side(b, false));
    runs.push({ left, right, a, b });
  }
  // A closed wall's joint is not an end: the run into it and the run out of it
  // are one stretch of wall.
  const last = runs[runs.length - 1];
  if (closed && runs.length >= 2 && runs[0].a.cap === 'start' && last.b.cap === 'end') {
    const first = runs.shift()!;
    last.left.push(...first.left.slice(1));
    last.right.push(...first.right.slice(1));
  }
  return runs.map((r) => [orient([...r.left, ...r.right.reverse()], 1)]);
}

function signedArea(pts: Vec2[]): number {
  let a = 0;
  for (let i = 0; i < pts.length; i++) {
    const p = pts[i], q = pts[(i + 1) % pts.length];
    a += p.x * q.y - q.x * p.y;
  }
  return a / 2;
}

/** `pts` running counter-clockwise (`dir` 1) or clockwise (-1). */
function orient(pts: Vec2[], dir: 1 | -1): Vec2[] {
  return Math.sign(signedArea(pts)) === -dir ? [...pts].reverse() : pts;
}

/** A drawn wall's door's swing (doorSwing.ts), and the side of the drawn line
 *  its leaf swings to: +1 the left, -1 the right. */
export type WallDoorSwing = DoorSwing & { side: 1 | -1 };

/** A door's leaf, drawn open square to the wall from its hinge jamb on the face
 *  it swings to, and the arc back to the far jamb. */
export function wallDoorSwing(sp: WallFeatureSpan): WallDoorSwing {
  const { fr, s, e, f } = sp;
  const side: 1 | -1 = f.swingRight ? -1 : 1;
  const off = side > 0 ? fr.oL : -fr.oR;
  const hinge = segPoint(fr, f.hingeEnd ? e : s, off);
  const closed = segPoint(fr, f.hingeEnd ? s : e, off);
  const r = e - s;
  return { hinge, closed, radius: r, side, open: { x: hinge.x + fr.n.x * side * r, y: hinge.y + fr.n.y * side * r } };
}

/** A feature's top (mm above the floor): a door's leaf height (2100), an opening's
 *  architrave (2100), a pane's top (the wall's own top — glazed to the ceiling). */
export function wallFeatureTop(w: WallLike, f: Pick<WallFeature, 'kind' | 'height'>): number {
  if (f.height != null) return f.height;
  return f.kind === 'door' ? WALL_DOOR_HEIGHT : f.kind === 'opening' ? WALL_OPENING_TOP : wallBase(w) + wallHeight(w);
}

/** A feature's bottom: an opening's sill (1000), a pane's base (the wall's own). */
export function wallFeatureBase(w: WallLike, f: Pick<WallFeature, 'kind' | 'heightFromGround'>): number {
  if (f.kind === 'door') return wallBase(w);
  if (f.heightFromGround != null) return f.heightFromGround;
  return f.kind === 'opening' ? WALL_OPENING_SILL : wallBase(w);
}

/** What still stands over a feature, bottom to top, inside the wall's own
 *  `[base, top]`: the wall above a door, the wall below and above an opening,
 *  and for a pane the wall below it, the glass, and the wall above it. */
export function wallFeatureLayers(w: WallLike, f: WallFeature): Array<{ z0: number; z1: number; glass?: boolean }> {
  const b = wallBase(w), T = b + wallHeight(w);
  const z = (v: number): number => Math.min(T, Math.max(b, v));
  const out: Array<{ z0: number; z1: number; glass?: boolean }> = [];
  const push = (z0: number, z1: number, glass = false): void => {
    const lo = z(z0), hi = z(z1);
    if (hi - lo > 0.5) out.push(glass ? { z0: lo, z1: hi, glass } : { z0: lo, z1: hi });
  };
  const top = wallFeatureTop(w, f), base = wallFeatureBase(w, f);
  if (f.kind === 'door') push(top, T);
  else if (f.kind === 'glass') { push(b, base); push(base, top, true); push(top, T); }
  else { push(b, base); push(top, T); }
  return out;
}

export interface WallPiece {
  fp: Vec2[];
  z0: number;
  z1: number;
  glass?: boolean;
  /** A pane's own colour. */
  color?: string;
  /** The feature it stands over — a pane's glass, a door's lintel, the wall below
   *  and above an opening or a pane —, so a section's click and box find the
   *  feature; the wall beside them is the wall's. */
  feature?: string;
  /** The part whose hole through the wall it stands over or under: a section
   *  cutting them draws them as one solid, no seam between them. */
  pocket?: string;
  /** The parts' pockets cut into it, not through it: the piece is still the
   *  whole wall (an elevation shows its face whole, no seam), a section cutting
   *  one draws the wall with that pocket a notch in it. */
  notches?: WallNotch[];
}

/** A pocket cut into a piece of wall (`WallPiece.notches`): its plan quad
 *  (`wallPocketQuad`), its heights, its part. */
export interface WallNotch {
  fp: Vec2[];
  z0: number;
  z1: number;
  pocket: string;
}

/** The wall in 3D pieces for a SECTION: one per segment stretch (unmitred, so each
 *  sorts by its own depth), the stretch over a feature split into its layers. A
 *  wall with no features gives exactly one full-height band per segment. */
export function wallSectionPieces(w: WallLike, pockets: readonly WallPocket[] = []): WallPiece[] {
  if (w.style === 'line') return [];
  const b = wallBase(w), T = b + wallHeight(w);
  const spans = wallFeatureSpans(w);
  const band = wallBand(w.points, w.thickness, w.align);
  const out: WallPiece[] = [];
  for (let i = 0; i + 1 < w.points.length; i++) {
    const segBand = wallBand([w.points[i], w.points[i + 1]], w.thickness, w.align);
    if (segBand.length < 3) continue;
    const mine = spans.filter((sp) => sp.seg === i);
    // A pocket through the wall is a hole in it, as an opening is; one cut into
    // it a notch in the whole wall's piece.
    const holes = pockets.filter((pk) => pk.seg === i && pk.through), notched = pockets.filter((pk) => pk.seg === i && !pk.through);
    const fr = mine[0]?.fr ?? wallSegFrame(w, i, band);
    if (!fr) { out.push({ fp: segBand, z0: b, z1: T }); continue; }
    const whole = (fp: Vec2[], s0: number, s1: number): WallPiece => {
      const notches = notched.filter((pk) => pk.s >= s0 - 0.5 && pk.e <= s1 + 0.5).map((pk) => ({ fp: wallPocketQuad(fr, pk), z0: pk.z0, z1: pk.z1, pocket: pk.src }));
      return notches.length ? { fp, z0: b, z1: T, notches } : { fp, z0: b, z1: T };
    };
    if (!mine.length && !holes.length) { out.push(whole(segBand, 0, fr.len)); continue; }
    // Doors, panes and openings, and the holes through it, in order along it.
    const items = [...mine.map((sp) => ({ s: sp.s, e: sp.e, sp })), ...holes.map((pk) => ({ s: pk.s, e: pk.e, pk }))].sort((p, q) => p.s - q.s);
    let cur = 0;
    for (const it of items) {
      if (it.s - cur > 0.5) out.push(whole(wallFeatureQuad({ fr, s: cur, e: it.s }), cur, it.s));
      if ('sp' in it) {
        const sp = it.sp, feature = sp.f.id ? { feature: sp.f.id } : {};
        for (const l of wallFeatureLayers(w, sp.f)) {
          out.push(l.glass ? { fp: wallPaneQuad(sp), ...l, ...(sp.f.color ? { color: sp.f.color } : {}), ...feature } : { fp: wallFeatureQuad(sp), ...l, ...feature });
        }
      } else {
        // Over a hole through it: the whole wall below and above it.
        const pk = it.pk, column = wallFeatureQuad({ fr, s: pk.s, e: pk.e }), pocket = pk.src;
        out.push({ fp: column, z0: b, z1: pk.z0, pocket }, { fp: column, z0: pk.z1, z1: T, pocket });
      }
      cur = it.e;
    }
    if (fr.len - cur > 0.5) out.push(whole(wallFeatureQuad({ fr, s: cur, e: fr.len }), cur, fr.len));
  }
  return out;
}

export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

/** One flat face of a wall's 3D mesh: a polygon with holes in the face's own 2D
 *  frame, `at` placing a frame point in the world (plan x, y and height z), and
 *  the outward `normal`. */
export interface WallFace {
  contour: Vec2[];
  holes: Vec2[][];
  at: (p: Vec2) => Vec3;
  normal: Vec3;
}

/** The heights over a feature where the wall is OPEN, inside `[base, top]` — the
 *  gaps between `wallFeatureLayers`' solid layers (a pane's glass is an opening
 *  in the wall; the pane is its own body). */
export function wallFeatureVoids(w: WallLike, f: WallFeature): Array<[number, number]> {
  const b = wallBase(w), T = b + wallHeight(w);
  const solid = wallFeatureLayers(w, f).filter((l) => !l.glass).sort((p, q) => p.z0 - q.z0);
  const out: Array<[number, number]> = [];
  let cur = b;
  for (const l of solid) { if (l.z0 - cur > 0.5) out.push([cur, l.z0]); cur = Math.max(cur, l.z1); }
  if (T - cur > 0.5) out.push([cur, T]);
  return out;
}

/**
 * A rectangle `[t0, t1] × [b, T]` with rectangular voids taken out, as polygons
 * with holes: a void touching the bottom or the top is a notch in the outline, one
 * spanning the whole height splits the rectangle, any other is a hole. Each
 * polygon is ONE face, so a wall's surface over a lintel or a sill is not pieced.
 */
function rectMinusVoids(t0: number, t1: number, b: number, T: number, voids: Array<{ s: number; e: number; z0: number; z1: number }>): Array<{ contour: Vec2[]; holes: Vec2[][] }> {
  const eps = 0.5;
  const sorted = [...voids].sort((p, q) => p.s - q.s);
  const out: Array<{ contour: Vec2[]; holes: Vec2[][] }> = [];
  let from = t0;
  const piece = (pa: number, pb: number, vs: typeof sorted): void => {
    if (pb - pa < eps) return;
    const bottom = vs.filter((v) => v.z0 <= b + eps), top = vs.filter((v) => v.z1 >= T - eps);
    const contour: Vec2[] = [{ x: pa, y: b }];
    for (const v of bottom) contour.push({ x: v.s, y: b }, { x: v.s, y: v.z1 }, { x: v.e, y: v.z1 }, { x: v.e, y: b });
    contour.push({ x: pb, y: b }, { x: pb, y: T });
    for (const v of [...top].reverse()) contour.push({ x: v.e, y: T }, { x: v.e, y: v.z0 }, { x: v.s, y: v.z0 }, { x: v.s, y: T });
    contour.push({ x: pa, y: T });
    const holes = vs.filter((v) => v.z0 > b + eps && v.z1 < T - eps)
      .map((v) => [{ x: v.s, y: v.z0 }, { x: v.s, y: v.z1 }, { x: v.e, y: v.z1 }, { x: v.e, y: v.z0 }]);
    out.push({ contour, holes });
  };
  let group: typeof sorted = [];
  for (const v of sorted) {
    if (v.z0 <= b + eps && v.z1 >= T - eps) { piece(from, v.s, group); group = []; from = v.e; }
    else group.push(v);
  }
  piece(from, t1, group);
  return out;
}

/**
 * A band wall as the flat faces of ONE closed mesh: per segment its two side
 * faces (each a single polygon running corner mitre to corner mitre, with the
 * doors, panes and openings cut out of it) and the reveals inside each opening,
 * then the top and the bottom (the plan outline at that height, cut where an
 * opening reaches it) and, on an open wall, its two end caps. Coplanar surfaces
 * are one face, so nothing is drawn where a lintel or a sill meets the wall.
 */
export function wallMeshFaces(w: WallLike, pockets: readonly WallPocket[] = []): WallFace[] {
  const p = w.points, m = p.length;
  const b = wallBase(w), T = b + wallHeight(w);
  if (m < 2 || T - b < 0.5) return [];
  const band = wallBand(p, w.thickness, w.align);
  if (band.length !== 2 * m) return [];
  const spans = wallFeatureSpans(w);
  const faces: WallFace[] = [];
  const v3 = (q: Vec2, z: number): Vec3 => ({ x: q.x, y: q.y, z });
  const flat = (n: Vec2, z = 0): Vec3 => ({ x: n.x, y: n.y, z });
  const frames: Array<WallSegFrame | null> = [];
  for (let i = 0; i + 1 < m; i++) {
    const fr = wallSegFrame(w, i, band);
    frames.push(fr);
    if (!fr) continue;
    const along = (q: Vec2): number => (q.x - fr.a.x) * fr.u.x + (q.y - fr.a.y) * fr.u.y;
    const voids = spans.filter((sp) => sp.seg === i)
      .flatMap((sp) => wallFeatureVoids(w, sp.f).map(([z0, z1]) => ({ s: sp.s, e: sp.e, z0, z1 })));
    // A part's pocket opens in the face it is cut into (both, through).
    const holes = pockets.filter((pk) => pk.seg === i);
    for (const [off, t0, t1, sign] of [
      [fr.oL, along(band[i]), along(band[i + 1]), 1],
      [-fr.oR, along(band[2 * m - 1 - i]), along(band[2 * m - 2 - i]), -1],
    ] as const) {
      const open = [...voids, ...holes.filter((pk) => pk.through || pk.side === sign)];
      for (const r of rectMinusVoids(t0, t1, b, T, open)) {
        faces.push({ ...r, at: (q) => v3(segPoint(fr, q.x, off), q.y), normal: flat({ x: fr.n.x * sign, y: fr.n.y * sign }) });
      }
    }
    // The reveals: both jambs, the head and the sill, across the wall's
    // thickness — a pocket's only as deep as it is, its back closing it.
    const reveals = (v: { s: number; e: number; z0: number; z1: number }, across: readonly [number, number]): void => {
      const jamb = (t: number, sign: number): WallFace => ({
        contour: [{ x: across[0], y: v.z0 }, { x: across[1], y: v.z0 }, { x: across[1], y: v.z1 }, { x: across[0], y: v.z1 }],
        holes: [], at: (q) => v3(segPoint(fr, t, q.x), q.y), normal: flat({ x: fr.u.x * sign, y: fr.u.y * sign }),
      });
      faces.push(jamb(v.s, 1), jamb(v.e, -1));
      const level = (z: number, up: number): WallFace => ({
        contour: [{ x: v.s, y: across[0] }, { x: v.e, y: across[0] }, { x: v.e, y: across[1] }, { x: v.s, y: across[1] }],
        holes: [], at: (q) => v3(segPoint(fr, q.x, q.y), z), normal: { x: 0, y: 0, z: up },
      });
      if (v.z1 < T - 0.5) faces.push(level(v.z1, -1));
      if (v.z0 > b + 0.5) faces.push(level(v.z0, 1));
    };
    for (const v of voids) reveals(v, [-fr.oR, fr.oL]);
    for (const pk of holes) {
      if (pk.through) { reveals(pk, [-fr.oR, fr.oL]); continue; }
      const back = pk.side > 0 ? fr.oL - pk.depth : -fr.oR + pk.depth;
      reveals(pk, pk.side > 0 ? [back, fr.oL] : [-fr.oR, back]);
      faces.push({
        contour: [{ x: pk.s, y: pk.z0 }, { x: pk.e, y: pk.z0 }, { x: pk.e, y: pk.z1 }, { x: pk.s, y: pk.z1 }],
        holes: [], at: (q) => v3(segPoint(fr, q.x, back), q.y), normal: flat({ x: fr.n.x * pk.side, y: fr.n.y * pk.side }),
      });
    }
  }
  // Top and bottom: the plan outline, cut only where an opening reaches that height.
  for (const [z, up] of [[T, 1], [b, -1]] as const) {
    const reaching = spans.filter((sp) => wallFeatureVoids(w, sp.f).some(([z0, z1]) => z0 <= z + 0.5 && z1 >= z - 0.5)).map((sp) => sp.f);
    for (const [outline, ...holes] of wallSolidRuns({ ...w, features: reaching })) {
      faces.push({ contour: outline, holes, at: (q) => v3(q, z), normal: { x: 0, y: 0, z: up } });
    }
  }
  if (!polylineIsClosed(p)) {
    const across = (fr: WallSegFrame): Vec2[] => [{ x: -fr.oR, y: b }, { x: fr.oL, y: b }, { x: fr.oL, y: T }, { x: -fr.oR, y: T }];
    const f0 = frames[0], f1 = frames[frames.length - 1];
    if (f0) faces.push({ contour: across(f0), holes: [], at: (q) => v3(segPoint(f0, 0, q.x), q.y), normal: flat({ x: -f0.u.x, y: -f0.u.y }) });
    if (f1) faces.push({ contour: across(f1), holes: [], at: (q) => v3(segPoint(f1, f1.len, q.x), q.y), normal: flat(f1.u) });
  }
  return faces;
}

/** The feature under a point: inside its cut (within `tol`), or — for a door —
 *  inside the square its leaf sweeps. */
export function wallFeatureAt(w: WallLike, p: Vec2, tol: number): WallFeatureSpan | null {
  const spans = wallFeatureSpans(w);
  for (let i = spans.length - 1; i >= 0; i--) {
    const sp = spans[i], fr = sp.fr;
    const t = (p.x - fr.a.x) * fr.u.x + (p.y - fr.a.y) * fr.u.y;
    const d = (p.x - fr.a.x) * fr.n.x + (p.y - fr.a.y) * fr.n.y;
    if (t < sp.s - tol || t > sp.e + tol) continue;
    if (d >= -fr.oR - tol && d <= fr.oL + tol) return sp;
    if (sp.f.kind === 'door') {
      const side = sp.f.swingRight ? -1 : 1;
      const out = side > 0 ? d - fr.oL : -fr.oR - d;
      if (out >= 0 && out <= sp.e - sp.s) return sp;
    }
  }
  return null;
}

/** Where a point lies along a band wall: the segment whose band it is in (within
 *  `tol` of either face), and its station along that segment, clamped to the
 *  straight run a feature may occupy. */
export function wallStationAt(w: WallLike, p: Vec2, tol: number): { seg: number; s: number; fr: WallSegFrame } | null {
  if (w.style === 'line' || w.points.length < 2) return null;
  const band = wallBand(w.points, w.thickness, w.align);
  let best: { seg: number; s: number; fr: WallSegFrame; d: number } | null = null;
  for (let i = 0; i + 1 < w.points.length; i++) {
    const fr = wallSegFrame(w, i, band);
    if (!fr) continue;
    const t = (p.x - fr.a.x) * fr.u.x + (p.y - fr.a.y) * fr.u.y;
    const d = (p.x - fr.a.x) * fr.n.x + (p.y - fr.a.y) * fr.n.y;
    if (t < -tol || t > fr.len + tol || d < -fr.oR - tol || d > fr.oL + tol) continue;
    const miss = Math.max(0, -t, t - fr.len);
    if (!best || miss < best.d) best = { seg: i, s: Math.max(fr.lo, Math.min(fr.hi, t)), fr, d: miss };
  }
  return best && { seg: best.seg, s: best.s, fr: best.fr };
}

/**
 * Add a feature over `[s, e]` on segment `seg` (mutates `w.features`). A pane or an
 * opening is EXTENDED over any of its own kind it touches (the first keeps its id
 * and settings) and gives way to the others: it keeps the longest stretch they
 * leave free. A door needs its stretch clear. Returns what was added or extended,
 * or null when nothing fits (under `WALL_FEATURE_MIN`, or a door that overlaps).
 */
export function addWallFeature(w: Wall, kind: WallFeature['kind'], seg: number, s: number, e: number, id: string): WallFeature | null {
  const fr = wallSegFrame(w, seg);
  if (!fr) return null;
  let a = Math.max(fr.lo, Math.min(s, e)), b = Math.min(fr.hi, Math.max(s, e));
  const min = kind === 'door' ? WALL_DOOR_MIN : WALL_FEATURE_MIN;
  if (b - a < min) return null;
  const all = w.features ?? [];
  const onSeg = all.filter((f) => f.seg === seg);
  const overlaps = (f: WallFeature, x: number, y: number, eps = 0): boolean =>
    Math.min(f.start, f.end) < y + eps && Math.max(f.start, f.end) > x - eps;
  if (kind === 'door') {
    if (onSeg.some((f) => overlaps(f, a, b))) return null;
    const f: WallFeature = { id, kind, seg, start: a, end: b };
    w.features = [...all, f];
    return f;
  }
  // Give way to the other kinds: the longest free stretch of [a, b].
  const blocks = onSeg.filter((f) => f.kind !== kind && overlaps(f, a, b))
    .map((f) => [Math.min(f.start, f.end), Math.max(f.start, f.end)] as [number, number])
    .sort((p, q) => p[0] - q[0]);
  let bestA = a, bestB = a, cur = a;
  for (const [x, y] of [...blocks, [b, b] as [number, number]]) {
    if (Math.min(x, b) - cur > bestB - bestA) { bestA = cur; bestB = Math.min(x, b); }
    cur = Math.max(cur, y);
  }
  a = bestA; b = bestB;
  if (b - a < min) return null;
  // Extend over its own kind.
  const same = onSeg.filter((f) => f.kind === kind && overlaps(f, a, b, 1));
  if (same.length) {
    const keep = same[0];
    const lo = Math.min(a, ...same.map((f) => Math.min(f.start, f.end)));
    const hi = Math.max(b, ...same.map((f) => Math.max(f.start, f.end)));
    keep.start = lo; keep.end = hi;
    w.features = all.filter((f) => f === keep || !same.includes(f));
    return keep;
  }
  const f: WallFeature = { id, kind, seg, start: a, end: b };
  w.features = [...all, f];
  return f;
}

/** Re-home features after the wall's centre line changed shape (a vertex added or
 *  removed, the wall exploded or joined): each goes to the new segment nearest its
 *  middle, at the stations its two ends project to. One further than `maxDist`
 *  from every new segment, or left with no room, is dropped. */
export function reprojectWallFeatures(oldPts: Vec2[], newPts: Vec2[], features: WallFeature[], maxDist = Infinity): WallFeature[] {
  const out: WallFeature[] = [];
  for (const f of features) {
    const a = oldPts[f.seg], b = oldPts[f.seg + 1];
    if (!a || !b) continue;
    const len = Math.hypot(b.x - a.x, b.y - a.y);
    if (len < 1e-6) continue;
    const u = { x: (b.x - a.x) / len, y: (b.y - a.y) / len };
    const P = (s: number): Vec2 => ({ x: a.x + u.x * s, y: a.y + u.y * s });
    const p0 = P(f.start), p1 = P(f.end), mid = P((f.start + f.end) / 2);
    let best = -1, bestD = Infinity;
    for (let j = 0; j + 1 < newPts.length; j++) {
      const d = distToSeg(mid, newPts[j], newPts[j + 1]);
      if (d < bestD - 1e-9) { bestD = d; best = j; }
    }
    if (best < 0 || bestD > maxDist) continue;
    const c = newPts[best], d = newPts[best + 1];
    const nl = Math.hypot(d.x - c.x, d.y - c.y);
    if (nl < 1e-6) continue;
    const along = (q: Vec2): number => Math.max(0, Math.min(nl, ((q.x - c.x) * (d.x - c.x) + (q.y - c.y) * (d.y - c.y)) / nl));
    const s0 = along(p0), s1 = along(p1);
    if (Math.abs(s1 - s0) < 1) continue;
    out.push({ ...f, seg: best, start: Math.min(s0, s1), end: Math.max(s0, s1) });
  }
  return out;
}

function distToSeg(p: Vec2, a: Vec2, b: Vec2): number {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy;
  const t = l2 > 0 ? Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2)) : 0;
  return Math.hypot(p.x - a.x - dx * t, p.y - a.y - dy * t);
}
